import { useCallback, useEffect, useMemo, useState, type ComponentProps } from "react";
import { Link } from "react-router";
import { CheckCircle2, ChevronDown, ChevronUp, Circle, Copy, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { useBackgroundTasksOptional } from "@/lib/background-tasks";
import {
  contactTypeLabel,
  genderLabel,
  listWecomExternalContacts,
  type WecomExternalContact,
} from "@/lib/wecom-external-contacts";
import { cn } from "@/lib/utils";

/** 敏感字段默认 password 屏蔽，点眼睛切换明文 */
function RevealableInput({ className, ...props }: ComponentProps<typeof Input>) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="relative">
      <Input
        {...props}
        type={revealed ? "text" : "password"}
        className={cn("pr-10", className)}
        autoComplete={props.autoComplete ?? "off"}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute top-1/2 right-0.5 size-8 -translate-y-1/2 text-muted-foreground"
        onClick={() => setRevealed((v) => !v)}
        aria-label={revealed ? "隐藏内容" : "显示内容"}
        tabIndex={-1}
      >
        {revealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </Button>
    </div>
  );
}

type ChecklistItem = {
  key: string;
  title: string;
  description: string;
};

type CustomDomainPayload = {
  host: string;
  status: string;
  error_message: string | null;
  cname_target: string;
  cname_name: string;
  verify_filename: string | null;
  verify_content: string | null;
  sidebar_url: string | null;
  oauth_redirect_url: string | null;
  trusted_domain: string | null;
  verify_url: string | null;
};

type WecomConnection = {
  bound: boolean;
  external_contact: boolean;
  callback: boolean;
  msgaudit: boolean;
  ready: boolean;
  corp_id: string | null;
  agent_id: string | null;
  secret_configured: boolean;
  callback_url: string | null;
  sidebar_url?: string | null;
  oauth_redirect_url?: string | null;
  trusted_domain?: string | null;
  custom_domain?: CustomDomainPayload | null;
  edge_cname_target?: string | null;
  /** 仅账户管理员回显；普通成员为 undefined */
  callback_token?: string | null;
  encoding_aes_key?: string | null;
  callback_token_configured?: boolean;
  encoding_aes_key_configured?: boolean;
  contacts_synced_at: string | null;
  external_contacts_count: number;
  egress_ips?: string[];
  /** 服务端根据本次请求识别的客户端公网 IP */
  client_ip?: string | null;
  setup_guides?: ChecklistItem[];
  checklist?: ChecklistItem[];
};

function domainStatusLabel(status: string, errorMessage: string | null) {
  switch (status) {
    case "pending_dns":
      return "等待 DNS";
    case "issuing_cert":
      return "签发证书中";
    case "active":
      return "已 HTTPS";
    case "error":
      return errorMessage || "异常";
    default:
      return status;
  }
}

type StatusBadgeProps = {
  label: string;
  ok: boolean;
};

function StatusBadge({ label, ok }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium",
        ok ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground",
      )}
    >
      {ok ? <CheckCircle2 className="size-3.5" aria-hidden /> : <Circle className="size-3.5" aria-hidden />}
      {label}
    </span>
  );
}

type SyncContactsResponse = WecomConnection & {
  accepted?: boolean;
  task?: { id: number; status: string; kind: string };
};

export default function WecomConnectionPage() {
  const { accountId, accessKind, brands } = useAccountWorkspace();
  const backgroundTasks = useBackgroundTasksOptional();
  const canManage = accessKind === "membership";
  const [connection, setConnection] = useState<WecomConnection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [contacts, setContacts] = useState<WecomExternalContact[]>([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsTotalPages, setContactsTotalPages] = useState(1);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [syncResultsOpen, setSyncResultsOpen] = useState(false);

  const syncActive = Boolean(backgroundTasks?.isKindActive("wecom_sync_contacts")) || syncing;

  const journeyCustomersHref = useMemo(() => {
    const journeyBrand =
      brands.find((brand) => brand.tools?.some((tool) => tool.product === "journey")) ?? brands[0];
    if (!journeyBrand) return null;
    return `/accounts/${accountId}/brands/${journeyBrand.id}/apps/journey/customers`;
  }, [accountId, brands]);

  const [corpId, setCorpId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [secret, setSecret] = useState("");
  const [callbackToken, setCallbackToken] = useState("");
  const [encodingAesKey, setEncodingAesKey] = useState("");
  const [wecomHost, setWecomHost] = useState("");
  const [verifyFilename, setVerifyFilename] = useState("");
  const [verifyContent, setVerifyContent] = useState("");
  const [savingDomain, setSavingDomain] = useState(false);
  const [savingVerify, setSavingVerify] = useState(false);
  const [probing, setProbing] = useState(false);

  const applyConnection = useCallback((payload: WecomConnection) => {
    setConnection(payload);
    setWecomHost(payload.custom_domain?.host ?? "");
    setVerifyFilename(payload.custom_domain?.verify_filename ?? "");
    setVerifyContent(payload.custom_domain?.verify_content ?? "");
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = await api.fetchForAccount<WecomConnection>(accountId, "/account/wecom_connection");
      applyConnection(payload);
      setCorpId(payload.corp_id ?? "");
      setAgentId(payload.agent_id ?? "");
      setSecret("");
      setCallbackToken(payload.callback_token ?? "");
      setEncodingAesKey(payload.encoding_aes_key ?? "");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法加载企微接入状态");
    } finally {
      setIsLoading(false);
    }
  }, [accountId, applyConnection]);

  const loadContacts = useCallback(
    async (page = 1) => {
      setContactsLoading(true);
      try {
        const payload = await listWecomExternalContacts(accountId, {
          page,
          perPage: 20,
        });
        setContacts(payload.external_contacts ?? []);
        setContactsPage(payload.pagination?.page ?? page);
        setContactsTotalPages(Math.max(1, payload.pagination?.total_pages ?? 1));
        if (payload.pagination?.total != null) {
          setConnection((prev) =>
            prev ? { ...prev, external_contacts_count: payload.pagination.total } : prev,
          );
        }
      } catch {
        // 列表失败不阻断配置页
      } finally {
        setContactsLoading(false);
      }
    },
    [accountId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadContacts(1);
  }, [loadContacts]);

  const syncTaskSucceededId = backgroundTasks?.tasks.find(
    (t) => t.kind === "wecom_sync_contacts" && t.status === "succeeded",
  )?.id;

  useEffect(() => {
    if (!syncTaskSucceededId) return;
    void load();
    void loadContacts(1);
  }, [syncTaskSucceededId, load, loadContacts]);

  async function persistConfig(opts?: { silent?: boolean }) {
    const body: Record<string, string> = {
      corp_id: corpId.trim(),
      agent_id: agentId.trim(),
      callback_token: callbackToken.trim(),
      encoding_aes_key: encodingAesKey.trim(),
    };
    if (secret.trim()) body.secret = secret.trim();

    const payload = await api.fetchForAccount<WecomConnection>(accountId, "/account/wecom_connection", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    applyConnection(payload);
    setSecret("");
    if (!opts?.silent) toast.success("已保存企微配置");
    return payload;
  }

  async function saveCredentials() {
    if (!canManage) return;
    setSaving(true);
    try {
      await persistConfig();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  async function verifyConnection() {
    if (!canManage) return;
    setVerifying(true);
    try {
      await persistConfig({ silent: true });
      const payload = await api.fetchForAccount<WecomConnection>(accountId, "/account/wecom_connection/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      applyConnection(payload);
      toast.success("连接检测成功");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "连接检测失败");
    } finally {
      setVerifying(false);
    }
  }

  async function syncContacts() {
    if (!canManage) return;
    setSyncing(true);
    try {
      const payload = await api.fetchForAccount<SyncContactsResponse>(
        accountId,
        "/account/wecom_connection/sync_contacts",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        },
      );
      applyConnection(payload);
      if (payload.accepted) {
        toast.success("已开始同步，进度见右上角任务面板");
      } else {
        toast.message("同步进行中，请稍候");
      }
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "同步失败");
    } finally {
      setSyncing(false);
    }
  }

  async function saveWecomHost() {
    if (!canManage) return;
    setSavingDomain(true);
    try {
      const payload = await api.fetchForAccount<WecomConnection>(accountId, "/account/wecom_connection", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wecom_host: wecomHost.trim() }),
      });
      applyConnection(payload);
      toast.success(wecomHost.trim() ? "已保存域名，正在下发边缘路由" : "已解除域名绑定");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "保存域名失败");
    } finally {
      setSavingDomain(false);
    }
  }

  async function saveVerifyFile() {
    if (!canManage || !connection?.custom_domain) return;
    setSavingVerify(true);
    try {
      const payload = await api.fetchForAccount<WecomConnection>(accountId, "/account/wecom_connection", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          verify_filename: verifyFilename.trim(),
          verify_content: verifyContent,
        }),
      });
      applyConnection(payload);
      toast.success("已保存校验文件");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "保存校验文件失败");
    } finally {
      setSavingVerify(false);
    }
  }

  async function probeDomain() {
    if (!canManage || !connection?.custom_domain) return;
    setProbing(true);
    try {
      const payload = await api.fetchForAccount<WecomConnection>(accountId, "/account/wecom_connection/probe_domain", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      applyConnection(payload);
      const status = payload.custom_domain?.status;
      if (status === "active") {
        toast.success("域名已 HTTPS 就绪");
      } else {
        toast.message(payload.custom_domain?.error_message || domainStatusLabel(status ?? "", null));
      }
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "检测域名失败");
    } finally {
      setProbing(false);
    }
  }

  async function copyCallbackUrl() {
    if (!connection?.callback_url) return;
    try {
      await navigator.clipboard.writeText(connection.callback_url);
      toast.success("回调 URL 已复制");
    } catch {
      toast.error("复制失败，请手动选择复制");
    }
  }

  async function copyText(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} 已复制`);
    } catch {
      toast.error("复制失败，请手动选择复制");
    }
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">正在加载企微接入…</p>;
  }

  if (error) {
    return (
      <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
        {error}
      </p>
    );
  }

  if (!connection) return null;

  const syncedLabel = connection.contacts_synced_at
    ? new Date(connection.contacts_synced_at).toLocaleString("zh-CN")
    : "尚未同步";

  return (
    <div className="space-y-8">
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to={`/accounts/${accountId}/channels`} className="text-primary-readable hover:underline">
            ← 渠道
          </Link>
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">企微接入</h1>
        <p className="text-sm text-muted-foreground">
          先绑定已备案独立域名，再配置自建应用凭证与回调。完成后 Journey 等产品即可启用。
        </p>
      </div>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="font-medium">企微专用域名</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            先保存客户已备案域名，CNAME 到平台后再点「检测域名」。解析成功才会部署边缘路由。
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="wecom_host">1. 域名</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="wecom_host"
              value={wecomHost}
              onChange={(e) => setWecomHost(e.target.value)}
              disabled={!canManage || savingDomain}
              placeholder="wecom.example.com"
              autoComplete="off"
              className="font-mono text-sm"
            />
            <Button type="button" variant="outline" disabled={!canManage || savingDomain} onClick={() => void saveWecomHost()}>
              {savingDomain ? "保存中…" : "保存域名"}
            </Button>
          </div>
        </div>

        {connection.custom_domain ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <p className="text-sm font-medium">2. CNAME 名称</p>
                <div className="flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1.5">
                  <code className="min-w-0 flex-1 truncate font-mono text-xs">{connection.custom_domain.cname_name}</code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0"
                    aria-label="复制 CNAME 名称"
                    onClick={() => void copyText("CNAME 名称", connection.custom_domain!.cname_name)}
                  >
                    <Copy className="size-3.5" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">CNAME 目标</p>
                <div className="flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1.5">
                  <code className="min-w-0 flex-1 truncate font-mono text-xs">{connection.custom_domain.cname_target}</code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0"
                    aria-label="复制 CNAME 目标"
                    onClick={() => void copyText("CNAME 目标", connection.custom_domain!.cname_target)}
                  >
                    <Copy className="size-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium">3. 状态</p>
              <span
                className={cn(
                  "inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium",
                  connection.custom_domain.status === "active"
                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {domainStatusLabel(connection.custom_domain.status, connection.custom_domain.error_message)}
              </span>
              {connection.custom_domain.error_message && connection.custom_domain.status !== "error" ? (
                <p className="text-xs text-muted-foreground">{connection.custom_domain.error_message}</p>
              ) : null}
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium">4. 主体校验文件</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="verify_filename">文件名</Label>
                  <Input
                    id="verify_filename"
                    value={verifyFilename}
                    onChange={(e) => setVerifyFilename(e.target.value)}
                    disabled={!canManage || savingVerify}
                    placeholder="WW_verify_xxxx.txt"
                    className="font-mono text-xs"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="verify_content">文件内容</Label>
                  <Input
                    id="verify_content"
                    value={verifyContent}
                    onChange={(e) => setVerifyContent(e.target.value)}
                    disabled={!canManage || savingVerify}
                    placeholder="企微后台给出的校验串"
                    className="font-mono text-xs"
                    autoComplete="off"
                  />
                </div>
              </div>
              <Button type="button" variant="outline" disabled={!canManage || savingVerify} onClick={() => void saveVerifyFile()}>
                {savingVerify ? "保存中…" : "保存校验文件"}
              </Button>
            </div>

            <div>
              <Button type="button" disabled={!canManage || probing} onClick={() => void probeDomain()}>
                {probing ? "检测中…" : "5. 检测域名"}
              </Button>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">6. 填进企微后台</p>
              <ul className="space-y-2 text-sm">
                {[
                  ["可信域名", connection.custom_domain.trusted_domain],
                  ["工具栏 URL", connection.custom_domain.sidebar_url],
                  ["OAuth URL", connection.custom_domain.oauth_redirect_url],
                  ["回调 URL", connection.callback_url],
                  ["校验文件 URL", connection.custom_domain.verify_url],
                ].map(([label, value]) => (
                  <li key={label} className="flex items-center gap-2 rounded-md border border-border/70 bg-muted/20 px-2 py-1.5">
                    <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
                    {value ? (
                      <>
                        <code className="min-w-0 flex-1 truncate font-mono text-xs">{value}</code>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7 shrink-0"
                          aria-label={`复制 ${label}`}
                          onClick={() => void copyText(label, value)}
                        >
                          <Copy className="size-3.5" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">保存校验文件后生成</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : (
          <p className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
            保存后把域名 CNAME 到 {connection.edge_cname_target || "app.c456.com"}，再点「检测域名」。未解析成功不会部署到边缘。
          </p>
        )}
      </section>

      <section className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-medium">就绪状态</h2>
            <p className="mt-1 text-sm text-muted-foreground">需同时完成绑定、回调校验与外部联系人同步。</p>
          </div>
          <span
            className={cn(
              "rounded-md px-3 py-1 text-sm font-medium",
              connection.ready
                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "bg-muted text-muted-foreground",
            )}
          >
            {connection.ready ? "已就绪" : "未就绪"}
          </span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <StatusBadge label="企业绑定" ok={connection.bound} />
          <StatusBadge label="回调配置" ok={connection.callback} />
          <StatusBadge label="外部联系人" ok={connection.external_contact} />
          <StatusBadge label="会话存档 · 后续开通" ok={false} />
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="font-medium">应用凭证</h2>
          <p className="mt-1 text-sm text-muted-foreground">填写自建应用的 CorpID、AgentId 与 Secret，然后检测连接。</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="corp_id">CorpID</Label>
            <Input
              id="corp_id"
              value={corpId}
              onChange={(e) => setCorpId(e.target.value)}
              disabled={!canManage || saving || verifying}
              placeholder="ww…"
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="agent_id">AgentId</Label>
            <Input
              id="agent_id"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              disabled={!canManage || saving || verifying}
              placeholder="1000002"
              autoComplete="off"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="secret">Secret</Label>
            <Input
              id="secret"
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              disabled={!canManage || saving || verifying}
              placeholder={connection.secret_configured ? "已配置，留空表示不修改" : "应用 Secret"}
              autoComplete="new-password"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={!canManage || saving || verifying} onClick={() => void saveCredentials()}>
            {saving ? "保存中…" : "保存凭证"}
          </Button>
          <Button type="button" disabled={!canManage || saving || verifying} onClick={() => void verifyConnection()}>
            {verifying ? "检测中…" : "检测连接"}
          </Button>
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="font-medium">企业可信 IP</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            企微限制只有白名单内的 IP 才能调用 API。未配置时，检测连接 / 同步客户会失败（错误码 60020）。
          </p>
        </div>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>打开企业微信管理后台</li>
          <li>安全与管理 → 管理工具 → IP 信息 →「企业可信 IP」</li>
          <li>将下方 IP 加入白名单并保存</li>
        </ol>

        <div className="space-y-2">
          <p className="text-sm font-medium">平台出口 IP（生产同步必加）</p>
          <div className="flex flex-wrap gap-2">
            {(connection.egress_ips?.length ? connection.egress_ips : ["106.75.21.253"]).map((ip) => (
              <div key={ip} className="flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1">
                <code className="font-mono text-sm">{ip}</code>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`复制 ${ip}`}
                  onClick={() => void copyText("IP", ip)}
                >
                  <Copy className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">当前公网 IP（本地开发调试用）</p>
          <p className="text-xs text-muted-foreground">
            由本平台根据你访问 API 的请求识别；仅在本机直连企微 API 时可能需要加入白名单。生产同步用上方平台出口 IP。
          </p>
          {connection.client_ip ? (
            <div className="flex w-fit items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1">
              <code className="font-mono text-sm">{connection.client_ip}</code>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label="复制当前公网 IP"
                onClick={() => void copyText("IP", connection.client_ip!)}
              >
                <Copy className="size-3.5" />
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">未能识别客户端 IP</p>
          )}
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="font-medium">事件回调</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            将下方 URL、Token、EncodingAESKey 填入企微后台并保存，完成 URL 校验后回调项会自动变真。
          </p>
        </div>
        <div className="space-y-2">
          <Label>回调 URL</Label>
          <div className="flex gap-2">
            <Input
              readOnly
              value={connection.callback_url ?? ""}
              placeholder="绑定域名后自动生成"
              className="font-mono text-xs"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => void copyCallbackUrl()}
              aria-label="复制回调 URL"
              disabled={!connection.callback_url}
            >
              <Copy className="size-4" />
            </Button>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="callback_token">Token</Label>
            <RevealableInput
              id="callback_token"
              value={callbackToken}
              onChange={(e) => setCallbackToken(e.target.value)}
              disabled={!canManage || saving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="encoding_aes_key">EncodingAESKey</Label>
            <RevealableInput
              id="encoding_aes_key"
              value={encodingAesKey}
              onChange={(e) => setEncodingAesKey(e.target.value)}
              disabled={!canManage || saving}
              placeholder="43 位字符"
            />
          </div>
        </div>
        <Button type="button" variant="outline" disabled={!canManage || saving} onClick={() => void saveCredentials()}>
          保存回调配置
        </Button>
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="font-medium">同步结果</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            后台异步拉取客户列表与昵称、头像、性别等资料；进度见右上角任务面板。
            须先在企微将本自建应用加入「客户联系 → API → 可调用接口的应用」。
          </p>
        </div>

        <Button
          type="button"
          disabled={!canManage || syncActive || !connection.bound}
          onClick={() => void syncContacts()}
        >
          {syncActive ? "同步中…" : "同步外部联系人"}
        </Button>
        {!connection.bound ? (
          <p className="text-xs text-muted-foreground">需先完成「检测连接」后再同步。</p>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            共 {connection.external_contacts_count} 人 · 上次同步 {syncedLabel}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2"
            onClick={() => setSyncResultsOpen((open) => !open)}
            aria-expanded={syncResultsOpen}
          >
            {syncResultsOpen ? (
              <>
                <ChevronUp className="size-3.5" aria-hidden />
                收起
              </>
            ) : (
              <>
                <ChevronDown className="size-3.5" aria-hidden />
                展开
              </>
            )}
          </Button>
          {journeyCustomersHref ? (
            <Link to={journeyCustomersHref} className="text-primary-readable hover:underline">
              在客户旅程中查看
            </Link>
          ) : null}
        </div>

        {syncResultsOpen ? (
          <>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full min-w-[40rem] text-left text-sm">
                <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">客户</th>
                    <th className="px-3 py-2 font-medium">性别</th>
                    <th className="px-3 py-2 font-medium">类型</th>
                    <th className="px-3 py-2 font-medium">跟进成员</th>
                    <th className="px-3 py-2 font-medium">external_userid</th>
                  </tr>
                </thead>
                <tbody>
                  {contactsLoading && contacts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                        加载中…
                      </td>
                    </tr>
                  ) : null}
                  {!contactsLoading && contacts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                        尚未同步或暂无客户
                      </td>
                    </tr>
                  ) : null}
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {c.avatar_url ? (
                            <img
                              src={c.avatar_url}
                              alt=""
                              className="size-8 rounded-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <span className="flex size-8 items-center justify-center rounded-full bg-muted text-xs text-muted-foreground">
                              {(c.name || "?").slice(0, 1)}
                            </span>
                          )}
                          <span className="font-medium">{c.name || "未获取昵称"}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{genderLabel(c.gender)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{contactTypeLabel(c.contact_type)}</td>
                      <td className="max-w-[10rem] truncate px-3 py-2 text-muted-foreground" title={c.follow_userids.join(", ")}>
                        {c.follow_userids.length > 0 ? c.follow_userids.join(", ") : "—"}
                      </td>
                      <td className="max-w-[12rem] truncate px-3 py-2 font-mono text-xs text-muted-foreground" title={c.external_userid}>
                        {c.external_userid}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {contactsTotalPages > 1 ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={contactsPage <= 1 || contactsLoading}
                  onClick={() => void loadContacts(contactsPage - 1)}
                >
                  上一页
                </Button>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {contactsPage} / {contactsTotalPages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={contactsPage >= contactsTotalPages || contactsLoading}
                  onClick={() => void loadContacts(contactsPage + 1)}
                >
                  下一页
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </section>

      <section className="space-y-3 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="font-medium">企微后台其它必配</h2>
          <p className="mt-1 text-sm text-muted-foreground">按路径在企微后台完成即可，无需在本页打勾。</p>
        </div>
        <ul className="space-y-3">
          {(connection.setup_guides ?? connection.checklist ?? [])
            .filter((item) => item.key !== "trusted_ip")
            .map((item) => (
              <li key={item.key} className="rounded-md border border-border/70 bg-muted/20 px-3 py-2">
                <div className="font-medium text-sm">{item.title}</div>
                <p className="mt-0.5 text-sm text-muted-foreground">{item.description}</p>
              </li>
            ))}
        </ul>
      </section>

      <section className="rounded-lg border border-dashed border-border bg-muted/30 p-5">
        <h2 className="font-medium text-muted-foreground">会话存档</h2>
        <p className="mt-1 text-sm text-muted-foreground">后续开通，当前不阻塞就绪状态。</p>
      </section>

      {!canManage ? (
        <p className="text-xs text-muted-foreground">仅账户管理员可修改企微接入配置。</p>
      ) : null}
    </div>
  );
}
